import { Injectable } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { BackendService } from './general/backend.service';
import { UserService } from './user.service';
import { FeedbackReportMetadata } from '../models/feedback-report.model';

const USERREPORTS_ENDPOINT = 'userreports';

@Injectable({
  providedIn: 'root',
})
export class FeedbackReportService {
  constructor(
    private backend: BackendService,
    private userService: UserService
  ) {}

  /**
   * Raccoglie i metadata richiesti per la segnalazione.
   * username può essere vuoto se l'utente non è loggato.
   */
  collectMetadata(entityId?: string | number): FeedbackReportMetadata {
    const userInfo = this.userService?.userInfo;
    const userDetails = this.userService?.userDetails;

    const username =
      userDetails?.first_name && userDetails?.last_name
        ? `${userDetails.first_name} ${userDetails.last_name}`
        : userInfo?.email ?? '';

    return {
      username,
      url: typeof window !== 'undefined' ? window.location.href : '',
      route: this.getCurrentRoute(),
      timestamp: new Date().toISOString(),
      browser: this.detectBrowser(),
      os: this.detectOs(),
      viewport: this.getViewport(),
      appVersion: environment.version ?? 'unknown',
      ...(entityId != null && { entityId }),
    };
  }

  /**
   * Invia la segnalazione al backend.
   * Usa BackendService.post con JSON: commento, metadata, screenshot (base64 se presente).
   */
  submitSegnalazione(
    commento: string,
    screenshot: Blob | null,
    metadata: FeedbackReportMetadata
  ): Observable<unknown> {
    const payload$ = screenshot
      ? from(this.blobToBase64(screenshot)).pipe(
          map((base64) => ({ commento, metadata, screenshot: base64 }))
        )
      : of({ commento, metadata });

    return payload$.pipe(
      switchMap((payload) => this.backend.post(USERREPORTS_ENDPOINT, payload)),
      catchError((error) => {
        this.backend.showErrors(error);
        return of(null);
      })
    );
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        resolve(result.replace(/^data:image\/\w+;base64,/, ''));
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  private getCurrentRoute(): string {
    try {
      return window.location.hash?.replace('#', '') || '/';
    } catch {
      return '/';
    }
  }

  private detectBrowser(): string {
    const ua = navigator.userAgent;
    let browser = 'Unknown';
    if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Edg')) browser = 'Edge';
    else if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
    return browser;
  }

  private detectOs(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Win')) return 'Windows';
    if (ua.includes('Mac')) return 'macOS';
    if (ua.includes('Linux')) return 'Linux';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad'))
      return 'iOS';
    return 'Unknown';
  }

  private getViewport(): string {
    return `${window.innerWidth}x${window.innerHeight}`;
  }
}
